import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import './CalendarPage.css';
import { toast } from '../components/Toast';

const EVENT_COLORS = {
  LEAVE:    { bg: '#fff3cd', border: '#f0ad4e', text: '#856404' },
  MEETING:  { bg: '#d1ecf1', border: '#17a2b8', text: '#0c5460' },
  HOLIDAY:  { bg: '#d4edda', border: '#28a745', text: '#155724' },
  TRAINING: { bg: '#e2d9f3', border: '#6f42c1', text: '#4a235a' },
  OTHER:    { bg: '#f8d7da', border: '#dc3545', text: '#721c24' },
};

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function CalendarPage() {
  const today = new Date();
  const [year, setYear]       = useState(today.getFullYear());
  const [month, setMonth]     = useState(today.getMonth() + 1);
  const [events, setEvents]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [editEvent, setEditEvent]   = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [error, setError]     = useState('');
  const [participantSearch, setParticipantSearch] = useState('');

  const [form, setForm] = useState({
    title: '', description: '',
    startDateTime: '', endDateTime: '',
    eventType: 'OTHER', participantIds: []
  });

  useEffect(() => { fetchEvents(); }, [year, month]);
  useEffect(() => { fetchEmployees(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/calendar-events?year=${year}&month=${month}`);
      setEvents(res.data?.data || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/employees?size=500');
      const data = res.data?.employees || res.data?.data?.employees || res.data?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch { console.error('Failed to load employees'); }
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsForDay = (day) => {
    if (!day) return [];
    return events.filter(e => {
      const d = new Date(e.startDateTime);
      return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
    });
  };

  const isToday = (day) =>
    day && today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === day;

  const openCreate = (day) => {
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setEditEvent(null);
    setForm({
      title: '', description: '',
      startDateTime: `${dateStr}T09:00`,
      endDateTime:   `${dateStr}T10:00`,
      eventType: 'OTHER', participantIds: []
    });
    setParticipantSearch('');
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditEvent(e);
    setForm({
      title: e.title, description: e.description || '',
      startDateTime: e.startDateTime?.slice(0, 16),
      endDateTime:   e.endDateTime?.slice(0, 16),
      eventType: e.eventType,
      participantIds: e.participants?.map(p => String(p.id)) || []
    });
    setParticipantSearch('');
    setDetailEvent(null);
    setShowModal(true);
  };

  const handleFormChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleParticipant = (id) => {
    const sid = String(id);
    setForm(f => ({
      ...f,
      participantIds: f.participantIds.includes(sid)
        ? f.participantIds.filter(x => x !== sid)
        : [...f.participantIds, sid]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...form,
        participantIds: form.participantIds.map(Number)
      };
      if (editEvent) {
        await axios.put(`/calendar-events/${editEvent.id}`, payload);
        flash('Event updated');
      } else {
        await axios.post('/calendar-events', payload);
        flash('Event created — participants notified by email');
      }
      setShowModal(false);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`/calendar-events/${id}`);
      setDetailEvent(null);
      flash('Event deleted');
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const flash = (msg) => { toast.success(msg); };

  return (
    <div className="cal-container">
      {/* Header */}
      <div className="cal-header">
        <div>
          <h1 className="cal-title">Calendar</h1>
          <p className="cal-subtitle">Team events, leave and meetings</p>
        </div>
        <button className="cal-btn-primary" onClick={() => openCreate(today.getDate())}>+ New Event</button>
      </div>

      {/* Navigation */}
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <h2 className="cal-month-label">{MONTHS[month - 1]} {year}</h2>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      {/* Legend */}
      <div className="cal-legend">
        {Object.entries(EVENT_COLORS).map(([type, c]) => (
          <span key={type} className="cal-legend-item" style={{ background: c.bg, borderColor: c.border, color: c.text }}>
            {type}
          </span>
        ))}
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="cal-loading">Loading…</div>
      ) : (
        <div className="cal-grid">
          {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}
          {cells.map((day, i) => (
            <div
              key={i}
              className={`cal-cell ${day ? 'cal-cell-active' : 'cal-cell-empty'} ${isToday(day) ? 'cal-today' : ''}`}
              onClick={() => day && openCreate(day)}
            >
              {day && <span className="cal-date-num">{day}</span>}
              <div className="cal-events-list">
                {eventsForDay(day).map(ev => {
                  const c = EVENT_COLORS[ev.eventType] || EVENT_COLORS.OTHER;
                  return (
                    <div
                      key={ev.id}
                      className="cal-event-chip"
                      style={{ background: c.bg, borderColor: c.border, color: c.text }}
                      onClick={e => { e.stopPropagation(); setDetailEvent(ev); }}
                      title={ev.title}
                    >
                      {ev.title}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event detail panel */}
      {detailEvent && (
        <div className="cal-modal-backdrop" onClick={() => setDetailEvent(null)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h3>{detailEvent.title}</h3>
              <button className="cal-close" onClick={() => setDetailEvent(null)}>&times;</button>
            </div>
            <div className="cal-modal-body">
              <div className="cal-detail-grid">
                <span className="cal-detail-label">Type</span>
                <span className="cal-detail-value">
                  <span className="cal-event-chip" style={{ background: EVENT_COLORS[detailEvent.eventType]?.bg, color: EVENT_COLORS[detailEvent.eventType]?.text, borderColor: EVENT_COLORS[detailEvent.eventType]?.border }}>
                    {detailEvent.eventType}
                  </span>
                </span>
                <span className="cal-detail-label">Start</span>
                <span className="cal-detail-value">{new Date(detailEvent.startDateTime).toLocaleString('en-IN')}</span>
                <span className="cal-detail-label">End</span>
                <span className="cal-detail-value">{new Date(detailEvent.endDateTime).toLocaleString('en-IN')}</span>
                <span className="cal-detail-label">Organizer</span>
                <span className="cal-detail-value">{detailEvent.createdByName}</span>
              </div>
              {detailEvent.description && (
                <div className="cal-detail-section">
                  <span className="cal-detail-label">Details</span>
                  <div className="cal-detail-desc">{detailEvent.description}</div>
                </div>
              )}
              {detailEvent.participants?.length > 0 && (
                <div className="cal-detail-section">
                  <span className="cal-detail-label">Participants ({detailEvent.participants.length})</span>
                  <div className="cal-participants-list">
                    {detailEvent.participants.map(p => (
                      <span key={p.id} className="cal-participant-chip">{p.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="cal-modal-footer">
              <button className="cal-btn-danger" onClick={() => handleDelete(detailEvent.id)}>Delete</button>
              <button className="cal-btn-secondary" onClick={() => openEdit(detailEvent)}>Edit</button>
              <button className="cal-btn-primary" onClick={() => setDetailEvent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <div className="cal-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="cal-modal cal-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h3>{editEvent ? 'Edit Event' : 'New Event'}</h3>
              <button className="cal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="cal-modal-body">
                <div className="cal-form-group">
                  <label>Title *</label>
                  <input value={form.title} onChange={e => handleFormChange('title', e.target.value)}
                    placeholder="Event title" required />
                </div>
                <div className="cal-form-row">
                  <div className="cal-form-group">
                    <label>Start *</label>
                    <input type="datetime-local" value={form.startDateTime}
                      onChange={e => handleFormChange('startDateTime', e.target.value)} required />
                  </div>
                  <div className="cal-form-group">
                    <label>End *</label>
                    <input type="datetime-local" value={form.endDateTime}
                      onChange={e => handleFormChange('endDateTime', e.target.value)} required />
                  </div>
                </div>
                <div className="cal-form-group">
                  <label>Event Type</label>
                  <select value={form.eventType} onChange={e => handleFormChange('eventType', e.target.value)}>
                    {Object.keys(EVENT_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="cal-form-group">
                  <label>Description</label>
                  <textarea rows={3} value={form.description}
                    onChange={e => handleFormChange('description', e.target.value)}
                    placeholder="Optional details…" />
                </div>
                <div className="cal-form-group">
                  <label>Participants <span className="cal-hint">(email invite sent)</span></label>
                  <div className="cal-participant-toolbar">
                    <input
                      type="text"
                      placeholder="Search participants…"
                      value={participantSearch}
                      onChange={e => setParticipantSearch(e.target.value)}
                      className="cal-participant-search"
                    />
                    <label className="cal-select-all">
                      <input
                        type="checkbox"
                        checked={
                          employees
                            .filter(emp => (emp.userName || emp.name || emp.employeeCode || '').toLowerCase().includes(participantSearch.toLowerCase()))
                            .length > 0 &&
                          employees
                            .filter(emp => (emp.userName || emp.name || emp.employeeCode || '').toLowerCase().includes(participantSearch.toLowerCase()))
                            .every(emp => form.participantIds.includes(String(emp.id)))
                        }
                        onChange={e => {
                          const filtered = employees
                            .filter(emp => (emp.userName || emp.name || emp.employeeCode || '').toLowerCase().includes(participantSearch.toLowerCase()))
                            .map(emp => String(emp.id));
                          setForm(f => ({
                            ...f,
                            participantIds: e.target.checked
                              ? [...new Set([...f.participantIds, ...filtered])]
                              : f.participantIds.filter(id => !filtered.includes(id))
                          }));
                        }}
                      />
                      <span>Select all</span>
                    </label>
                  </div>
                  <div className="cal-participant-picker">
                    {employees
                      .filter(emp => {
                        const name = (emp.userName || emp.name || emp.employeeCode || '').toLowerCase();
                        return name.includes(participantSearch.toLowerCase());
                      })
                      .map(emp => {
                        const id = String(emp.id);
                        const name = emp.userName || emp.name || emp.employeeCode;
                        const checked = form.participantIds.includes(id);
                        return (
                          <label key={emp.id} className={`cal-participant-option ${checked ? 'selected' : ''}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleParticipant(emp.id)} />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    {employees.length === 0 && <span className="cal-muted">No employees found</span>}
                  </div>
                  {form.participantIds.length > 0 && (
                    <span className="cal-hint" style={{ marginTop: 4 }}>{form.participantIds.length} selected</span>
                  )}
                </div>
              </div>
              <div className="cal-modal-footer">
                <button type="button" className="cal-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="cal-btn-primary">
                  {editEvent ? 'Update' : 'Create & Notify'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;
